import { Grid, Flex, Text, Divider, Button } from "@chakra-ui/react"
import Header from "../../../app/components/Header"
import { SelectableCard } from "./components/SelectableCard"
import DateRangeInputs from '../../../app/components/DateRangePicker/DateRangeInputs'
import { useEffect, useState } from "react"
import { BillIcon } from "../../../assets/icons/BillIcon"
import { ConsumedEnergyIcon } from "../../../assets/icons/ConsumedEnergy"
import { IFormFields } from "../api/types"
import { useForm } from "react-hook-form"
import { startOfToday, startOfYear, subDays } from 'date-fns'
import { EnergyConsumptionReportFilters } from "../forms/EnergyConsumptionReportFilters"
import { TrendReportFilters } from "../forms/TrendReportFilters"
import { BillingReportFilters } from "../forms/BillingReportFilters"
import { ReportDataIcon } from "../../../assets/icons/ReportData"
import { useLocation, useNavigate } from "react-router-dom";

export enum ReportPeriodType {
    Day = 'Day',
    Month = 'Month',
}

export default function GenerateReport() {
    const [selectedReportType, setSelectedReportType] = useState<number | null>(null)
    const navigate = useNavigate()

    const {
        register,
        watch,
        setValue,
        formState: { errors },
    } = useForm<IFormFields>()

    const startDate = watch('startDate') || null
    const endDate = watch('endDate') || null
    const reportHistory = false

    const [selectedLocations, setSelectedLocations] = useState([])
    const [selectedGroups, setSelectedGroups] = useState([])
    const [selectedDevices, setSelectedDevices] = useState([])

    const handleReset = () => {
        setSelectedReportType(null)
        setValue('startDate', null)
        setValue('endDate', null)
        setSelectedLocations([])
        setSelectedGroups([])
        setSelectedDevices([])
    }

    const location = useLocation()
    const restoredFormData = location.state?.formData

    useEffect(() => {
        if (restoredFormData) {
            setSelectedReportType(restoredFormData.selectedReportType)
            setValue('startDate', new Date(restoredFormData.startDate))
            setValue('endDate', new Date(restoredFormData.endDate))
            setSelectedLocations(restoredFormData.selectedLocations)
            setSelectedGroups(restoredFormData.selectedGroups)
            setSelectedDevices(restoredFormData.selectedDevices)
        }
    }, [restoredFormData, setValue])

    async function onSubmit() {
        navigate('/reports/preview-report', {
            state: {
                formData: {
                    selectedReportType,
                    startDate,
                    endDate,
                    selectedLocations,
                    selectedGroups,
                    selectedDevices,
                    ibexPrice: watch('ibexPrice'),
                },
            },
        })
    }

    const isFormValid = () => {
        return !selectedReportType ||
            !startDate ||
            !endDate ||
            ((selectedReportType === 1 || selectedReportType === 3) && selectedLocations?.length === 0) ||
            (selectedReportType === 1 && (selectedDevices?.length === 0 && selectedGroups?.length === 0)) ||
            (selectedReportType === 2 && (selectedDevices?.length === 0 && selectedLocations?.length === 0)) ||
            (selectedReportType === 3 && (selectedDevices?.length === 0 && selectedGroups?.length === 0))
    }

    return (
        <>
            <Header title="Reports" />

            <Grid
                templateColumns={['1fr', null, '1fr 1fr']}
                columnGap={6}
                gap={6}
            >
                <Flex
                    direction="column"
                    alignItems="flex-end"
                    flexWrap="wrap-reverse"
                    gap={6}
                    bg="#FFFFFF"
                    borderRadius="20px"
                    padding="20px"
                >
                    {/* Header */}
                    <Text color="#484848" fontWeight={700} fontSize="20px" fontFamily="Inter">Generate Report</Text>
                    {/* Body */}
                    {/* Type */}
                    <Flex direction="column" gap={3}>
                        {/* Label */}
                        <Text color="#484848" fontWeight={700} fontSize="14px" fontFamily="Inter">Select Report Type</Text>
                        {/* Options */}
                        <Flex gap={3}>
                            <SelectableCard
                                icon={ConsumedEnergyIcon}
                                text="Energy Consumption"
                                description="Detailed energy usage across selected options."
                                isSelected={selectedReportType === 1}
                                onClick={() => {
                                    setSelectedReportType(1)
                                    setSelectedLocations([])
                                    setSelectedGroups([])
                                    setSelectedDevices([])
                                }}
                            />
                            {/* <SelectableCard
                                icon={TrendReportIcon}
                                text="Trend"
                                description="Identify patterns and trends in consumption over time."
                                isSelected={selectedReportType === 2}
                                onClick={() => {
                                    setSelectedReportType(2)
                                    setSelectedLocations([])
                                    setSelectedGroups([])
                                    setSelectedDevices([])
                                }}
                            /> */}
                            <SelectableCard
                                icon={BillIcon}
                                text="Billing"
                                description="Identify patterns and trends in consumption over time."
                                isSelected={selectedReportType === 3}
                                onClick={() => {
                                    setSelectedReportType(3)
                                    setSelectedLocations([])
                                    setSelectedGroups([])
                                    setSelectedDevices([])
                                }}
                            />
                        </Flex>
                    </Flex>
                    <Divider />
                    {/* Period */}
                    <Flex direction="column" gap={3}>
                        {/* Label */}
                        <Text color="#484848" fontWeight={700} fontSize="14px" fontFamily="Inter">Select Period</Text>
                        {/* @ts-ignore */}
                        <DateRangeInputs
                            register={register}
                            watch={watch}
                            setValue={setValue}
                            errors={errors}
                            selectedStartName="startDate"
                            selectedEndName="endDate"
                            startDate={startDate}
                            endDate={endDate}
                            minDate={startOfYear(subDays(startOfToday(), 365))}
                        />
                    </Flex>
                    <Divider />
                    {selectedReportType === 1 && <EnergyConsumptionReportFilters
                        selectedLocations={selectedLocations}
                        selectedGroups={selectedGroups}
                        selectedDevices={selectedDevices}
                        setSelectedLocations={setSelectedLocations}
                        setSelectedGroups={setSelectedGroups}
                        setSelectedDevices={setSelectedDevices}
                    />}
                    {selectedReportType === 2 && <TrendReportFilters
                        selectedLocations={selectedLocations}
                        selectedDevices={selectedDevices}
                        setSelectedLocations={setSelectedLocations}
                        setSelectedDevices={setSelectedDevices}
                    />}
                    {selectedReportType === 3 && <BillingReportFilters
                        register={register}
                        selectedLocations={selectedLocations}
                        selectedGroups={selectedGroups}
                        selectedDevices={selectedDevices}
                        setSelectedGroups={setSelectedGroups}
                        setSelectedLocations={setSelectedLocations}
                        setSelectedDevices={setSelectedDevices}
                    />}
                    {/* Footer */}
                    <Flex width="full" justifyContent="space-between">
                        <Button onClick={handleReset}>
                            Reset
                        </Button>
                        <Button colorScheme="blue" isDisabled={isFormValid()} onClick={onSubmit}>
                            Generate
                        </Button>
                    </Flex>
                </Flex>
                <Flex
                    direction="column"
                    gap={6}
                    height="550px"
                    bg="#FFFFFF"
                    borderRadius="20px"
                    padding="20px"
                >
                    {/* Header */}
                    <Text color="#484848" fontWeight={700} fontSize="20px" fontFamily="Inter">History</Text>
                    {reportHistory ? (
                        null
                    ) : (
                        <Flex direction="column" width="100%" height="100%" justifyContent="center" alignItems="center">
                            <ReportDataIcon color="grey" boxSize={10} />
                            <Text color="grey">Once you generate reports, they'll show up here</Text>
                        </Flex>
                    )}
                </Flex>
            </Grid>
        </>
    )
}